import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PlacementAnalyticsTab = () => {
  const [selectedYear, setSelectedYear] = useState('2025-2026');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const yearOptions = [
    { value: '2025-2026', label: 'Academic Year 2025-2026' },
    { value: '2024-2025', label: 'Academic Year 2024-2025' },
    { value: '2023-2024', label: 'Academic Year 2023-2024' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'cse', label: 'Computer Science' },
    { value: 'ece', label: 'Electronics & Communication' },
    { value: 'mech', label: 'Mechanical Engineering' }
  ];

  const placementStats = [
    {
      title: 'Overall Placement Rate',
      value: '87.5%',
      change: '+5.2%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'var(--color-success)'
    },
    {
      title: 'Average Package',
      value: '₹6.5 LPA',
      change: '+₹0.8L',
      changeType: 'positive',
      icon: 'IndianRupee',
      iconColor: 'var(--color-primary)'
    },
    {
      title: 'Highest Package',
      value: '₹24 LPA',
      change: '+₹3L',
      changeType: 'positive',
      icon: 'Award',
      iconColor: 'var(--color-accent)'
    },
    {
      title: 'Companies Visited',
      value: '156',
      change: '+12',
      changeType: 'positive',
      icon: 'Building2',
      iconColor: 'var(--color-warning)'
    }
  ];

  const departmentWiseData = [
    {
      department: 'Computer Science',
      totalStudents: 320,
      placed: 295,
      placementRate: 92.2,
      avgPackage: '₹7.2 LPA',
      highestPackage: '₹24 LPA'
    },
    {
      department: 'Electronics & Communication',
      totalStudents: 280,
      placed: 238,
      placementRate: 85.0,
      avgPackage: '₹6.5 LPA',
      highestPackage: '₹18 LPA'
    },
    {
      department: 'Mechanical Engineering',
      totalStudents: 250,
      placed: 210,
      placementRate: 84.0,
      avgPackage: '₹5.8 LPA',
      highestPackage: '₹15 LPA'
    },
    {
      department: 'Civil Engineering',
      totalStudents: 200,
      placed: 165,
      placementRate: 82.5,
      avgPackage: '₹5.2 LPA',
      highestPackage: '₹12 LPA'
    }
  ];

  const topRecruiters = [
    {
      id: 1,
      company: 'Tech Innovations Pvt Ltd',
      studentsHired: 45,
      avgPackage: '₹8.5 LPA',
      roles: ['Software Engineer', 'Data Analyst']
    },
    {
      id: 2,
      company: 'DataTech Solutions',
      studentsHired: 32,
      avgPackage: '₹7.8 LPA',
      roles: ['Full Stack Developer', 'DevOps Engineer']
    },
    {
      id: 3,
      company: 'Global Solutions India',
      studentsHired: 28,
      avgPackage: '₹7.2 LPA',
      roles: ['Frontend Developer', 'UI/UX Designer']
    },
    {
      id: 4,
      company: 'Creative Solutions India',
      studentsHired: 25,
      avgPackage: '₹6.8 LPA',
      roles: ['Backend Developer', 'QA Engineer']
    }
  ];

  const salaryDistribution = [
    { range: '< ₹4 LPA', count: 45, percentage: 12 },
    { range: '₹4-6 LPA', count: 120, percentage: 32 },
    { range: '₹6-8 LPA', count: 135, percentage: 36 },
    { range: '₹8-12 LPA', count: 60, percentage: 16 },
    { range: '> ₹12 LPA', count: 15, percentage: 4 }
  ];

  const placementTrends = [
    { month: 'Aug 2025', placed: 25 },
    { month: 'Sep 2025', placed: 45 },
    { month: 'Oct 2025', placed: 68 },
    { month: 'Nov 2025', placed: 92 },
    { month: 'Dec 2025', placed: 115 },
    { month: 'Jan 2026', placed: 145 },
    { month: 'Feb 2026', placed: 165 }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          options={yearOptions}
          value={selectedYear}
          onChange={setSelectedYear}
          className="w-full md:w-64"
        />
        <Select
          options={departmentOptions}
          value={selectedDepartment}
          onChange={setSelectedDepartment}
          className="w-full md:w-64"
        />
        <Button variant="default" iconName="Download">
          Export Report
        </Button>
        <Button variant="outline" iconName="FileText">
          Generate PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {placementStats?.map((stat, index) => (
          <div key={index} className="bg-card rounded-xl p-4 border border-border shadow-warm-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">{stat?.title}</p>
                <h3 className="text-2xl font-heading font-bold text-card-foreground">{stat?.value}</h3>
              </div>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat?.iconColor}15` }}
              >
                <Icon name={stat?.icon} size={20} color={stat?.iconColor} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={stat?.changeType === 'positive' ? 'TrendingUp' : 'TrendingDown'} 
                size={16} 
                color={stat?.changeType === 'positive' ? 'var(--color-success)' : 'var(--color-error)'} 
              />
              <span className={`text-sm font-medium ${stat?.changeType === 'positive' ? 'text-success' : 'text-error'}`}>
                {stat?.change}
              </span>
              <span className="text-sm text-muted-foreground">vs last year</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-background rounded-lg p-6 border border-border">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Department-wise Placement Statistics</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Students</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Placed</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Placement Rate</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Avg. Package</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Highest Package</th>
              </tr>
            </thead>
            <tbody>
              {departmentWiseData?.map((dept, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">{dept?.department}</td>
                  <td className="py-3 px-4 text-sm text-foreground">{dept?.totalStudents}</td>
                  <td className="py-3 px-4 text-sm text-success font-medium">{dept?.placed}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden max-w-[100px]">
                        <div 
                          className="h-full bg-success transition-smooth"
                          style={{ width: `${dept?.placementRate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground">{dept?.placementRate}%</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{dept?.avgPackage}</td>
                  <td className="py-3 px-4 text-sm font-medium text-primary">{dept?.highestPackage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-background rounded-lg p-6 border border-border">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Top Recruiters</h3>
          <div className="space-y-3">
            {topRecruiters?.map((recruiter) => (
              <div key={recruiter?.id} className="p-4 rounded-lg border border-border hover:border-primary transition-smooth">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-medium text-foreground">{recruiter?.company}</h4>
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                    {recruiter?.studentsHired} hired
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Avg. Package: {recruiter?.avgPackage}</p>
                <div className="flex flex-wrap gap-1">
                  {recruiter?.roles?.map((role, idx) => (
                    <span key={idx} className="px-2 py-1 bg-muted text-xs text-foreground rounded">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background rounded-lg p-6 border border-border">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Salary Distribution</h3>
          <div className="space-y-3">
            {salaryDistribution?.map((range, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-foreground">{range?.range}</span>
                  <span className="text-sm font-medium text-foreground">{range?.count} students ({range?.percentage}%)</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-smooth"
                    style={{ width: `${range?.percentage * 2.5}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-background rounded-lg p-6 border border-border">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Placement Trends (Monthly)</h3>
        <div className="flex items-end justify-between gap-2 h-64">
          {placementTrends?.map((trend, index) => {
            const maxValue = Math.max(...placementTrends?.map(t => t?.placed));
            const height = (trend?.placed / maxValue) * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="text-xs font-medium text-foreground">{trend?.placed}</div>
                <div 
                  className="w-full bg-primary rounded-t-lg transition-smooth hover:bg-primary/80"
                  style={{ height: `${height}%` }}
                />
                <div className="text-xs text-muted-foreground text-center">{trend?.month?.split(' ')?.[0]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlacementAnalyticsTab;